import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LeedsCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Leeds Pothole Claims | GREEN Rated, AMBER Roads | Fixtyer"
        description="Leeds City Council earns an overall GREEN DfT rating but AMBER on road condition. Their own data shows RED A-roads more than doubled in 2024 and 26,007 potholes filled in 2024/25. See what that means for your claim."
        canonical="/councils/leeds"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Leeds', url: '/councils/leeds' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Leeds has an overall GREEN DfT rating — does that block my pothole claim?',
            answer: 'No. The overall GREEN rating reflects strong spend and best-practice scorecards alongside mid-table road condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on the council\'s headline DfT colour. Leeds\' own data shows classified RED roads more than doubled in 2024 and 65,104 potholes filled in five years.',
          },
          {
            question: 'Why is Leeds GREEN overall but AMBER on condition?',
            answer: 'The DfT weights spend, process and best practice alongside condition. Leeds matches its DfT capital allocation with similar council capital (£18.3m each in 2025/26), spends 87.2% preventatively, and holds a £288.3m backlog it is actively funding down. That earns GREEN on spend and best practice. Road condition is only AMBER because SCANNER data shows RED A-roads rising from 2.8% to 5.1% and RED B/C roads from 2.6% to 5.9% between 2023 and 2024.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 2,342km — 78% of Leeds\' 3,004km carriageway network. Engineers Inspection surveys cover just over 25% of U-roads each year, meaning the full network is assessed on a four-year cycle. At the latest survey, 7.9% of U-roads were RED — roughly 185km of residential and estate routes. The council itself links the 2023 spike in RED U-roads to extreme weather and admits it faces "the challenge to manage a declining network."',
          },
          {
            question: 'Does Leeds\' £288.3m maintenance backlog help my claim?',
            answer: 'It strengthens context, not automatic liability. Leeds publicly estimates a £288.3m backlog and allocates its own capital alongside DfT funding to address it. That admission shows the council knows deterioration outpaces budget — but you still need evidence the specific defect was reportable, visible, and left unrepaired within their inspection intervals.',
          },
          {
            question: 'Leeds filled 26,007 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Leeds filled 26,007 potholes in 2024/25 — up from 7,938 in 2020/21 — while simultaneously reporting more RED classified roads than at any point in the five-year data. The council states that "despite our best-efforts new potholes can quickly form, increase in size, and can often be found in clusters." Reactive volume proves defects form faster than planned works eliminate them.',
          },
          {
            question: 'How does Section 58 apply to a well-run GREEN council like Leeds?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Leeds\' GREEN best-practice rating, AI-assisted inspections, and 87.2% preventative spend make their defence stronger than most. But Section 58 is defect-specific: if your pothole met intervention criteria, was previously reported, or should have been found on a safety inspection before you hit it, the defence weakens regardless of headline ratings.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Leeds City Council"
        councilSlug="leeds"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/leeds"
        headline="Leeds Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Leeds</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Leeds: Green-Rated Process, Amber-Rated Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Leeds City Council earns an overall <strong className="text-emerald-400">GREEN</strong> DfT rating — 
            strong on spend and best practice — but road condition is only <strong className="text-amber-400">AMBER</strong>. 
            Their own transparency report shows RED classified roads more than doubled in 2024, a{' '}
            <strong className="text-rose-400">£288.3m backlog</strong>, and{' '}
            <strong className="text-rose-400">26,007 potholes filled in 2024/25 alone</strong> on a network where 
            78% of carriageway is unclassified residential roads.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">26,007</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 7,938 in 2020/21 — while RED A-roads rose from 2.8% to 5.1% and RED B/C roads 
              from 2.6% to 5.9% in the same period Leeds reports filling more potholes than ever.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Split Verdict: GREEN Overall, AMBER on the Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Leeds performs well on process and investment. The claim-relevant metric — physical road condition — sits at AMBER.
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Leeds City Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">What it means for claims</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">Above-average council — expect a organised Section 58 defence</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">Mid-table roads — the metric that actually matters for your pothole</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">£18.3m DfT capital matched by £18.2m council capital in 2025/26</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-sm text-surface-600">Well Managed Highways Code, asset management strategy, AI-assisted inspections</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <p className="text-emerald-800">
                <strong>What GREEN overall means:</strong> Leeds is not failing. It invests heavily, follows national asset management guidance, and earns GREEN on spend and best practice. Many councils rated worse still deny every claim — Leeds at least publishes honest condition data.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <p className="text-amber-800">
                <strong>What AMBER condition means for your claim:</strong> The DfT is telling you the roads themselves are only mid-table. Your claim does not turn on the council's process scorecard — it turns on whether the specific defect on your specific road was reasonably inspected and repaired under Section 41 of the Highways Act 1980.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            3,004km of Roads — 78% Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Leeds manages one of England's largest urban highway networks. Most of it is residential.
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Survey cycle</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">406 km</td>
                  <td className="px-6 py-4 text-center">13.5%</td>
                  <td className="px-6 py-4 text-surface-600">SCANNER — 50% surveyed annually (2-year full cycle)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">256 km</td>
                  <td className="px-6 py-4 text-center">8.5%</td>
                  <td className="px-6 py-4 text-surface-600">SCANNER — 50% surveyed annually (2-year full cycle)</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-bold">2,342 km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">78.0%</td>
                  <td className="px-6 py-4 text-amber-800">&gt;25% surveyed annually (4-year full cycle)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-bold">3,004 km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                  <td className="px-6 py-4 text-surface-600">Plus 5,619 km footways, 148 km cycleways</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why the 4-Year U-Road Cycle Matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              Leeds condition-surveys unclassified roads through Engineers Inspection (EIN) surveys covering just over 
              25% of the network each year. That means any given residential street may go up to four years between 
              formal condition assessments — even though safety inspections run separately.
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last EIN-surveyed — year one, two, three, or four of the cycle?</li>
              <li>• With 7.9% of U-roads in RED at the latest survey, was yours on the priority list?</li>
              <li>• Did a safety inspection catch the defect before you hit it, or only after?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN process rating does not mean every metre of every U-road is continuously monitored for structural condition.
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Five-Year Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER and EIN survey data from Leeds' own transparency report — classified RED roads spiking in 2024
          </p>

          {/* A roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (406 km): RED more than doubled in 2024</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">32.0%</td>
                    <td className="px-4 py-2 text-center">65.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">30.0%</td>
                    <td className="px-4 py-2 text-center">68.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">31.4%</td>
                    <td className="px-4 py-2 text-center">66.0%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">32.8%</td>
                    <td className="px-4 py-2 text-center">64.4%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.1%</td>
                    <td className="px-4 py-2 text-center font-bold">30.8%</td>
                    <td className="px-4 py-2 text-center font-bold">64.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED A-roads jumped from 2.8% to 5.1% in a single survey year — the highest RED share in the five-year dataset.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (256 km): same pattern, sharper spike</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                    <td className="px-4 py-2 text-center">29.7%</td>
                    <td className="px-4 py-2 text-center">68.6%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                    <td className="px-4 py-2 text-center">29.4%</td>
                    <td className="px-4 py-2 text-center">68.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">32.3%</td>
                    <td className="px-4 py-2 text-center">65.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">32.5%</td>
                    <td className="px-4 py-2 text-center">65.0%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.9%</td>
                    <td className="px-4 py-2 text-center font-bold">31.6%</td>
                    <td className="px-4 py-2 text-center font-bold">62.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C RED roads more than doubled from 2.6% to 5.9% between 2023 and 2024. Good-condition B/C roads 
              fell from 68.6% to 62.5% over the full five-year period.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-900 px-6 pt-6 mb-4">U roads (2,342 km): RED share — residential network</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">6.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center text-emerald-700">5.8%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8.2%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7.9%</td>
                </tr>
              </tbody>
            </table>
            <p className="px-6 pb-6 pt-4 text-surface-600 text-sm">
              At 7.9% RED, roughly 185 km of Leeds' unclassified network was in the worst condition category at the 
              latest survey — estate roads, residential streets, and village routes where most pothole damage occurs.
            </p>
          </div>

          <QuoteCard
            quote="The increase in red-rated U roads in 2023 reflects the impact of extreme weather events, competing demands on limited funding and an ageing infrastructure the result of which is the challenge to manage a declining network."
            source="Leeds City Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Following the Money
          </h2>
          <p className="text-surface-600 mb-8">
            Leeds matches DfT capital pound-for-pound with council capital — and still faces a £288.3m backlog
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">LCC capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">13,014,573</td>
                  <td className="px-4 py-3 text-center">12,300,000</td>
                  <td className="px-4 py-3 text-center">5,320,210</td>
                  <td className="px-4 py-3 text-center">82.6%</td>
                  <td className="px-4 py-3 text-center">17.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">10,758,681</td>
                  <td className="px-4 py-3 text-center">12,300,000</td>
                  <td className="px-4 py-3 text-center">5,327,932</td>
                  <td className="px-4 py-3 text-center">81.2%</td>
                  <td className="px-4 py-3 text-center">18.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">14,780,800</td>
                  <td className="px-4 py-3 text-center">15,000,000</td>
                  <td className="px-4 py-3 text-center">5,327,932</td>
                  <td className="px-4 py-3 text-center">84.8%</td>
                  <td className="px-4 py-3 text-center">15.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">20,048,648</td>
                  <td className="px-4 py-3 text-center">15,000,000</td>
                  <td className="px-4 py-3 text-center">5,327,932</td>
                  <td className="px-4 py-3 text-center">86.8%</td>
                  <td className="px-4 py-3 text-center">13.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">25,263,043</td>
                  <td className="px-4 py-3 text-center">11,150,200</td>
                  <td className="px-4 py-3 text-center">5,343,000</td>
                  <td className="px-4 py-3 text-center">87.2%</td>
                  <td className="px-4 py-3 text-center">12.8%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center font-bold">18,256,605</td>
                  <td className="px-4 py-3 text-center font-bold">18,242,900</td>
                  <td className="px-4 py-3 text-center">5,343,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">87.2%</td>
                  <td className="px-4 py-3 text-center">12.8%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="In addition to the capital funding that is received from the DfT, we have also allocated a significant amount of its own capital funding for many years. This reflects the scale of the backlog (estimated at £288.3M) and the high priority afforded to this type of work by the council, Members and the public."
            source="Leeds City Council Local Highways Maintenance Transparency Report 2025"
          />
          <p className="mt-6 text-surface-600 text-sm">
            Leeds resurfaced over 121 km of carriageway in 2024/25 and plans 124 km in 2025/26, funded via the West 
            Yorkshire CRSTS settlement (2022/23 to 2026/27). From 2026/27, condition surveys transition to BSI PAS2161 — 
            five categories instead of three — which will break comparability with the RED/Amber/Green data above.
          </p>
        </div>
      </section>

      {/* Pothole conveyor belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            65,104 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a network Leeds admits is declining
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,938</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,658</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">8,855</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">14,646</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26,007</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">65,104</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Unfortunately, despite our best-efforts new potholes can quickly form, increase in size, and can often be found in clusters occurring in a similar time frame."
            source="Leeds City Council Local Highways Maintenance Transparency Report 2025"
          />
          <p className="mt-6 text-surface-600 text-sm">
            Leeds links the 2023/24 spike to the second wettest year on record since 1836, with eleven named storms. 
            It has allocated £2m to review pothole repairs end-to-end and estimates filling approximately 15,000 potholes 
            in 2025/26 — still nearly double the 2020/21 baseline.
          </p>
        </div>
      </section>

      {/* Balanced claims assessment */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Run GREEN Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Leeds is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN overall, spend and best-practice scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Council capital matched to DfT allocation (£18.2m each in 2025/26)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 87.2% preventative maintenance spend — well above reactive
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy, AI-assisted inspections, CRSTS funding
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 121 km resurfaced in 2024/25 — genuine planned investment
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic "council neglect" arguments will fail.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — mid-table roads despite GREEN process scores
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED classified roads more than doubled in 2024 (A: 2.8%→5.1%, B/C: 2.6%→5.9%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "the challenge to manage a declining network"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £288.3m backlog — deterioration outpaces even matched funding
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 78% of network on 4-year U-road condition cycle; 7.9% RED at latest survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 65,104 potholes in five years — defects form in clusters between inspections
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong> under Section 41 and Section 58 of the Highways Act 1980:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 4-year EIN survey cycle is your strongest structural argument</li>
              <li>• Timing relative to the 2023 weather spike Leeds itself links to increased RED U-roads</li>
            </ul>
            <p className="text-white font-medium">
              This is not a failing council. But it is a council whose own data proves it cannot prevent every pothole — 
              and whose AMBER condition rating confirms the roads are only mid-table, not exemplary.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Leeds?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 4-year survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• Ignore the GREEN vs AMBER rating split</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ AMBER condition vs GREEN overall framed honestly</li>
                <li>✅ 2024 classified RED spike documented</li>
                <li>✅ Declining network admission cited verbatim</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Leeds</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=leeds"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Leeds Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Leeds has an overall GREEN DfT rating — does that block my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The overall GREEN rating reflects strong spend and best-practice scorecards alongside mid-table 
                road condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably 
                inspected and repaired — not on the council's headline DfT colour. Leeds' own data shows classified RED 
                roads more than doubled in 2024 and 65,104 potholes filled in five years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Leeds GREEN overall but AMBER on condition?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT weights spend, process and best practice alongside condition. Leeds matches its DfT capital 
                allocation with similar council capital (£18.3m each in 2025/26), spends 87.2% preventatively, and holds 
                a £288.3m backlog it is actively funding down. That earns GREEN on spend and best practice. Road condition 
                is only AMBER because SCANNER data shows RED A-roads rising from 2.8% to 5.1% and RED B/C roads from 
                2.6% to 5.9% between 2023 and 2024.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 2,342km — 78% of Leeds' 3,004km carriageway network. Engineers Inspection 
                surveys cover just over 25% of U-roads each year, meaning the full network is assessed on a four-year 
                cycle. At the latest survey, 7.9% of U-roads were RED — roughly 185km of residential and estate routes. 
                The council itself links the 2023 spike in RED U-roads to extreme weather and admits it faces "the 
                challenge to manage a declining network."
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Leeds' £288.3m maintenance backlog help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It strengthens context, not automatic liability. Leeds publicly estimates a £288.3m backlog and allocates 
                its own capital alongside DfT funding to address it. That admission shows the council knows deterioration 
                outpaces budget — but you still need evidence the specific defect was reportable, visible, and left 
                unrepaired within their inspection intervals.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Leeds filled 26,007 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Leeds filled 26,007 potholes in 2024/25 — up from 7,938 in 2020/21 — while simultaneously reporting 
                more RED classified roads than at any point in the five-year data. The council states that "despite our 
                best-efforts new potholes can quickly form, increase in size, and can often be found in clusters." Reactive 
                volume proves defects form faster than planned works eliminate them.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to a well-run GREEN council like Leeds?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. 
                Leeds' GREEN best-practice rating, AI-assisted inspections, and 87.2% preventative spend make their defence 
                stronger than most. But Section 58 is defect-specific: if your pothole met intervention criteria, was previously 
                reported, or should have been found on a safety inspection before you hit it, the defence weakens regardless 
                of headline ratings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.leeds.gov.uk/parking-roads-and-travel/road-improvement-schemes/annual-programme-of-works/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Leeds City Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
